import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CommandBar from "../components/CommandBar";
import { CALCULATOR_CATEGORIES } from "../data/calculatorCatalog";
import "./MainLayout.css";

const lowerLinks = [
  { label: "Formula Reference", to: "/formula-reference", badge: "FR" },
  { label: "Material Data", to: "/material-data", badge: "MD" },
  { label: "Resources", to: "/resources", badge: "RS" },
  { label: "What's New", to: "/whats-new", badge: "NEW" },
  { label: "About ManuCalc", to: "/about", badge: "AB" },
];

const MainLayout = ({
  children,
  showFooter = true,
  user,
  onLogout,
  preferences,
  accountProfile,
  isCompanyOwner = false,
  rolePermissions = {},
  isPlatformOwner = false,
}) => {
  const [commandOpen, setCommandOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("manucalc.sidebar.collapsed") === "true"
  );
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
  const currentSearch = typeof window !== "undefined" ? window.location.search : "";
  const activeCategory = new URLSearchParams(currentSearch).get("category") || "";

  const canAccessAdmin =
    Boolean(rolePermissions?.canUseAdmin) &&
    (isCompanyOwner ||
      (accountProfile?.accountType === "company" &&
        accountProfile?.role === "owner" &&
        accountProfile?.adminDashboardEnabled));
  const canAccessJobs = rolePermissions?.canUseJobs !== false;
  const canAccessExports = rolePermissions?.canExport === true;
  const isCompanySeatAccount = accountProfile?.accountBillingType === "companySeat";
  const closeMobileSidebar = () => setMobileSidebarOpen(false);
  const toggleDesktopSidebar = () => {
    setDesktopSidebarCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem("manucalc.sidebar.collapsed", String(next));
      return next;
    });
  };

  useEffect(() => {
    const handleKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen((previous) => !previous);
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  return (
    <div
      className={`app-shell ${desktopSidebarCollapsed ? "sidebar-collapsed" : ""} ${
        currentPath === "/ai-assistant" ? "ai-route-active" : ""
      } main-layout-dark`}
    >
      <CommandBar
        open={commandOpen}
        setOpen={setCommandOpen}
        preferences={preferences}
      />

      {mobileSidebarOpen && (
        <button
          type="button"
          className="app-sidebar-backdrop"
          aria-label="Close navigation"
          onClick={closeMobileSidebar}
        />
      )}

      <aside className={`app-sidebar ${mobileSidebarOpen ? "open" : ""}`}>
        <div className="app-brand-row">
          <Link to="/" className="app-brand" onClick={closeMobileSidebar}>
            <span className="app-brand-mark">MC</span>
            <span>
              <strong>ManuCalc</strong>
              <small>Manufacturing Math OS</small>
            </span>
          </Link>
          <button type="button" className="app-sidebar-collapse" onClick={toggleDesktopSidebar} title={desktopSidebarCollapsed ? "Expand navigation" : "Collapse navigation"}>
            {desktopSidebarCollapsed ? ">" : "<"}
          </button>
        </div>

        <nav className="app-sidebar-section" aria-label="Main navigation">
          <Link
            to="/"
            onClick={closeMobileSidebar}
            className={`app-nav-item ${currentPath === "/" ? "active" : ""}`}
          >
            <span>DB</span>
            Dashboard
          </Link>
          <Link
            to="/dashboard"
            onClick={closeMobileSidebar}
            className={`app-nav-item ${currentPath === "/dashboard" && !activeCategory ? "active" : ""}`}
          >
            <span>ALL</span>
            All Calculators
          </Link>
          {CALCULATOR_CATEGORIES.map((category) => (
            <Link
              key={category.id}
              to={`/dashboard?category=${category.id}`}
              onClick={closeMobileSidebar}
              className={`app-nav-item app-accent-${category.accent} ${
                activeCategory === category.id ? "active" : ""
              }`}
            >
              <span>{category.icon}</span>
              {category.name}
            </Link>
          ))}
        </nav>

        <nav className="app-sidebar-section app-sidebar-secondary" aria-label="Resources">
          {canAccessJobs && (
            <>
              <Link to="/jobs" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/jobs") ? "active" : ""}`}>
                <span>PLN</span>
                Manufacturing Plans
              </Link>
              <Link to="/collaborative-projects" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/collaborative-projects") ? "active" : ""}`}>
                <span>COL</span>
                Collaborative Projects
              </Link>
              <Link to="/process-builder" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/process-builder") ? "active" : ""}`}>
                <span>SOP</span>
                SOP Builder
              </Link>
              <Link to="/setup-sheets" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/setup-sheets") ? "active" : ""}`}>
                <span>SET</span>
                Setup Sheets
              </Link>
              <Link to="/job-travelers" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/job-travelers") ? "active" : ""}`}>
                <span>TRV</span>
                Job Travelers
              </Link>
              <Link to="/inspection" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/inspection") ? "active" : ""}`}>
                <span>QC</span>
                Inspection
              </Link>
              <Link to="/tools" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/tools") ? "active" : ""}`}>
                <span>TL</span>
                Tool Library
              </Link>
              <Link to="/fixtures" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/fixtures") ? "active" : ""}`}>
                <span>FX</span>
                Fixture Library
              </Link>
              <Link to="/machines" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/machines") ? "active" : ""}`}>
                <span>MC</span>
                Machine Database
              </Link>
              <Link to="/shop-floor" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/shop-floor") ? "active" : ""}`}>
                <span>SF</span>
                Shop Floor Mode
              </Link>
              <Link to="/shop-brain" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/shop-brain") ? "active" : ""}`}>
                <span>KB</span>
                Shop Brain
              </Link>
              <Link to="/capacity-planner" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/capacity-planner") ? "active" : ""}`}>
                <span>CAP</span>
                Capacity Planner
              </Link>
              <Link to="/qr-codes" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/qr-codes") ? "active" : ""}`}>
                <span>QR</span>
                QR Codes
              </Link>
              <Link to="/ai-assistant" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/ai-assistant") ? "active" : ""}`}>
                <span>AI</span>
                AI Assistant
              </Link>
              {accountProfile?.companyId && (
                <Link to="/messages" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/messages") ? "active" : ""}`}>
                  <span>MSG</span>
                  Company Messages
                </Link>
              )}
              {canAccessExports && (
                <Link to="/exports" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/exports") ? "active" : ""}`}>
                  <span>EXP</span>
                  Export Center
                </Link>
              )}
              <Link to="/3s" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/3s") ? "active" : ""}`}>
                <span>3S</span>
                3S Workspace
              </Link>
            </>
          )}
          {canAccessAdmin && (
            <Link to="/admin" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath.startsWith("/admin") ? "active" : ""}`}>
              <span>ADM</span>
              Admin Dashboard
            </Link>
          )}
          <Link to="/request-custom-tool" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath === "/request-custom-tool" ? "active" : ""}`}>
            <span>REQ</span>
            Request Custom Tool
          </Link>
          {isPlatformOwner && (
            <Link to="/owner/requests" onClick={closeMobileSidebar} className={`app-nav-item ${currentPath === "/owner/requests" || currentPath === "/platform-admin/feature-requests" ? "active" : ""}`}>
              <span>FRQ</span>
              Owner Request Board
            </Link>
          )}
          {lowerLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              onClick={closeMobileSidebar}
              className={`app-nav-item ${currentPath === link.to ? "active" : ""}`}
            >
              <span>{link.badge}</span>
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="app-sidebar-footer">
          {isCompanySeatAccount && (
            <div className="app-company-pill">
              <span>Connected</span>
              <strong>{accountProfile?.companyName || "Company Workspace"}</strong>
            </div>
          )}
          {accountProfile?.isFounder && (
            <div className="app-company-pill founder">
              <span>Founding Member</span>
              <strong>{accountProfile?.founderNumber ? `#${accountProfile.founderNumber}` : "Lifetime Access"}</strong>
            </div>
          )}
          <Link to="/settings" onClick={closeMobileSidebar} className="app-settings-pill">
            Settings
          </Link>
          {user ? (
            <button type="button" onClick={onLogout} className="app-logout-btn">
              Logout
            </button>
          ) : (
            <Link to="/auth" onClick={closeMobileSidebar} className="app-logout-btn">
              Sign In
            </Link>
          )}
          <small>ManuCalc v2.0</small>
        </div>
      </aside>

      <div className="app-main">
        <header className="app-topbar">
          <button
            type="button"
            className="app-mobile-menu"
            onClick={() => setMobileSidebarOpen(true)}
          >
            Menu
          </button>
          <button
            type="button"
            className="app-search-trigger"
            onClick={() => setCommandOpen(true)}
          >
            <span>Search calculators, jobs, formulas...</span>
            <kbd>Ctrl K</kbd>
          </button>
          <div className="app-topbar-actions">
            <button type="button" title="Favorites">Fav</button>
            <button type="button" title="History">Hist</button>
            <Link to="/settings" title="Settings">Set</Link>
          </div>
        </header>

        <main className="main-content">{children}</main>

        {showFooter && <div className="app-footer-spacer" />}
      </div>
    </div>
  );
};

export default MainLayout;
